import { Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../App';
import { ReactComponent as Right } from '../../../Assets/CommonTenant/right-arrow.svg';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { formatToINR } from '../../../Utils/utils';
import styles from './index.module.css';
import HeaderSubHeader from '../Header/HeaderSubHeader';
import NavigationHeader from '../Header/NavigationHeader';
import { formatCategoryName } from '../utils/savingsUtils';
import RecurringTransactionView from './RecurringTransactionView';
import BottomSheet from '../common/BottomSheet';

const RecurringTransactions: React.FC<{
  selectedAccounts: any;
  isFilterOptions?: boolean;
  isViewTransationVisible?: boolean;
  showAllPayments?: boolean;
  onGoback?: any;
}> = ({
  selectedAccounts,
  isFilterOptions = false,
  isViewTransationVisible = false,
  showAllPayments = false,
  onGoback,
}) => {
  const [allTransactions, setAllTransactions] = useState<any>([]);
  const { data, setBackButtonHandler }: any = useContext(AppContext);
  const [isViewAllTransactions, setIsViewAllTransactions] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [completedTxn, setCompletedTxn] = useState<any[]>([]);
  const [pendingTxn, setPendingTxn] = useState<any[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<any[]>([]);
  const navigate = useNavigate();
  const { Text } = Typography;

  // Process category breakdown from transactions
  const processCategoryBreakdown = (transactions: any[]) => {
    const categories: {
      [key: string]: {
        count: number;
        totalAmount: number;
        transactions: any[];
      };
    } = {};

    transactions.forEach((txn: any) => {
      const category = txn.categoryCode || txn.category || 'OTHERS';
      if (!categories[category]) {
        categories[category] = { count: 0, totalAmount: 0, transactions: [] };
      }
      categories[category].count += 1;
      categories[category].totalAmount += txn.lastAmount || 0;
      categories[category].transactions.push(txn);
    });

    return Object.entries(categories).map(([category, data]) => ({
      category: formatCategoryName(category),
      originalCategory: category,
      count: data.count,
      totalAmount: data.totalAmount,
      transactions: data.transactions,
    }));
  };

  useEffect(() => {
    if (isViewTransationVisible) {
      window.onpopstate = () => {
        // Push the user forward again to prevent back
        window.history.pushState(null, '', window.location.href);
      };
      setBackButtonHandler(() => {
        return () => {
          pushEventToParent({
            eventCode: EVENTCODES.BACK_BUTTON_RECURRING,
          });
          setIsViewAllTransactions(false);
          onGoback && onGoback();
        };
      });
    }
  }, [isViewTransationVisible, onGoback, setBackButtonHandler]);

  const getTransactionApiCall = useCallback(() => {
    const monthlyTransactions =
      data?.current?.ASSET?.recurringAnalysis?.MONTHLY ?? [];
    const filteredData = monthlyTransactions
      .filter((txn: any) => {
        const accountMatches = selectedAccounts
          ? txn.account.slice(-6) === selectedAccounts.accNo
          : true;
        return accountMatches && txn.active;
      })
      .sort(
        (a: any, b: any) =>
          dayjs(a.lastTransactionDate).valueOf() -
          dayjs(b.lastTransactionDate).valueOf()
      );

    const today = dayjs();
    const currentDate = today.date();
    const currentMonth = today.month() + 1;

    const {
      completedTxnLocal,
      pendingTxnLocal,
      totalAmount,
      totalPaidAmount,
      totalPendingAmount,
    } = filteredData.reduce(
      (acc: any, txn: any) => {
        const txnDate = dayjs(txn.lastTransactionDate);

        const isCompleted =
          currentMonth === txnDate.month() + 1 && currentDate > txnDate.date();

        if (isCompleted) {
          acc.completedTxnLocal.push(txn);
          acc.totalPaidAmount += txn.lastAmount;
        } else {
          acc.pendingTxnLocal.push(txn);
          acc.totalPendingAmount += txn.lastAmount;
        }

        acc.totalAmount += txn.lastAmount;

        return acc;
      },
      {
        completedTxnLocal: [],
        pendingTxnLocal: [],
        totalAmount: 0,
        totalPaidAmount: 0,
        totalPendingAmount: 0,
      }
    );

    setCompletedTxn(completedTxnLocal);
    setPendingTxn(pendingTxnLocal);
    setTotalAmount(totalAmount);
    setTotalPaidAmount(totalPaidAmount);
    setTotalPendingAmount(totalPendingAmount);
    setAllTransactions(
      isFilterOptions ? filteredData : filteredData.slice(0, 4)
    );
    setCategoryBreakdown(processCategoryBreakdown(filteredData));
  }, [data, selectedAccounts, isFilterOptions]);

  useEffect(() => {
    getTransactionApiCall();
  }, [getTransactionApiCall]);

  if (!isFilterOptions && allTransactions.length === 0 && !showAllPayments) {
    return <></>;
  }
  return (
    <>
      <div
        className={`${styles.transactionContainer} transactionContainer ${
          isFilterOptions ? styles.padding24 : ''
        }`}
      >
        {isViewAllTransactions || showAllPayments ? (
          <BottomSheet
            isOpen={isViewAllTransactions || showAllPayments}
            onClose={() => {}}
            onTap={() => {}}
            detent='full-height'
            draggable={false}
            className='recurringTransactionsSheet'
          >
            <div className={`${styles.allTxn}`}>
              <NavigationHeader
                header={'My Recurring Payments'}
                onBack={() => {
                  if (isViewTransationVisible || showAllPayments) {
                    pushEventToParent({
                      eventCode: EVENTCODES.BACK_BUTTON_RECURRING,
                    });
                    setIsViewAllTransactions(false);
                    onGoback && onGoback();
                  }
                }}
              />

              <RecurringTransactions
                selectedAccounts={selectedAccounts}
                isFilterOptions={true}
              />
            </div>
          </BottomSheet>
        ) : (
          <>
            <HeaderSubHeader header='We detected the following recurring payments for your account' />
            <div
              className={
                isFilterOptions
                  ? styles.transactionSectionFiltered
                  : styles.transactionSection
              }
            >
              <RecurringTransactionView
                isFilterOptions={isFilterOptions}
                allTransactions={allTransactions}
                completedTxn={completedTxn}
                pendingTxn={pendingTxn}
                categoryBreakdown={categoryBreakdown}
                totalPendingAmount={totalPendingAmount}
                totalPaidAmount={totalPaidAmount}
              />

              {isViewTransationVisible && allTransactions.length > 0 && (
                <div
                  onClick={() => {
                    navigate('/recurringPayments');
                    pushEventToParent({
                      eventCode: EVENTCODES.VIEW_ALL_RECURRING,
                    });
                    setIsViewAllTransactions(true);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: 'var(--primary-color)',
                    fontSize: 12,
                    width: 'auto',
                    fontWeight: 600,
                    marginTop: 16,
                  }}
                >
                  View all{' '}
                  <Right
                    style={{ marginLeft: 4, color: 'var(--primary-color)' }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isFilterOptions && totalAmount && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px',
            background: 'var(--ct-bg-card)',
          }}
        >
          <Text
            style={{
              color: 'var(--pb-neutral)',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            TOTAL
          </Text>
          <Text style={{ fontSize: 16, fontWeight: 600 }}>
            {formatToINR(totalAmount.toString())}
          </Text>
        </div>
      )}
    </>
  );
};

export default RecurringTransactions;
